"use client";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
	faClock,
	faDice,
	faEnvelope,
	faExternalLinkAlt,
	faGamepad,
	faMapMarkerAlt,
	faStar,
	faTicketAlt,
	faUsers,
	faUtensils,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

const RolEnReplayPage = () => {
	const [showCopyNotification, setShowCopyNotification] = useState(false);

	const experienceFeatures = [
		{
			id: "game",
			title: "La Partida",
			icon: faGamepad,
			description: {
				paragraphs: [
					<>
						Una <strong>aventura de 4 horas</strong> dirigida por un Amo del Calabozo experimentado, diseñada
						para que disfrutes al máximo sin preocuparte por nada. Elige entre nuestros sistemas disponibles o
						déjanos saber tus preferencias.
					</>,
					<>
						No importa si eres un jugador experimentado o si es tu primera vez. Nuestros directores de juego
						profesionales adaptarán la partida a tu nivel y preferencias, asegurando una experiencia memorable
						para todos.
					</>,
				],
			},
			features: [
				"4 horas de juego con tiempo extra para la cena",
				"Sistemas adaptables a todos los niveles",
				"Material de juego incluido (dados, fichas, etc.)",
				"Aventuras personalizables según tus gustos",
				"Ambiente relajado y sin prisas",
			],
			image: "/images/rol-en-replay-game.webp",
			color: "border-red-500",
			accentColor: "bg-red-600",
			textColor: "text-red-400",
		},
		{
			id: "food",
			title: "El Festín",
			icon: faUtensils,
			description: {
				paragraphs: [
					<>
						<strong>Deliciosas hamburguesas</strong> preparadas con ingredientes de calidad, acompañadas de
						nachos con los mejores toppings y tu bebida favorita. Todo incluido en el precio.
					</>,
					<>
						Mientras tus personajes se enfrentan a desafíos épicos, tú podrás disfrutar de un banquete digno
						de los héroes más legendarios. ¡Porque toda gran aventura necesita un buen festín!
					</>,
				],
			},
			features: [
				"Hamburguesas de chuparse los dedos (opción vegetariana disponible)",
				"Nachos con toppings deliciosos, a tope de queso",
				"Bebida incluida (refrescos, cerveza o bebida sin alcohol)",
				"Menú diseñado especialmente para jugadores de rol",
				"Posibilidad de añadir los extras que te apetezcan",
			],
			image: "/images/rol-en-replay-food.webp",
			color: "border-yellow-500",
			accentColor: "bg-yellow-600",
			textColor: "text-yellow-400",
		},
		{
			id: "venue",
			title: "El Local",
			icon: faMapMarkerAlt,
			description: {
				paragraphs: [
					<>
						El evento se realiza en el{" "}
						<strong>
							<a
								href="https://www.replayoutletcafe.com/"
								target="_blank"
								rel="noopener noreferrer"
								className="text-red-400 hover:text-red-300 underline"
							>
								Replay Boardgame Café
								<FontAwesomeIcon icon={faExternalLinkAlt} className="ml-1 text-xs" />
							</a>
						</strong>
						, un espacio acogedor especializado en juegos de mesa, situado en pleno corazón de Madrid (
						<a
							href="https://www.google.com/maps?q=Replay+Boardgame+Caf%C3%A9"
							target="_blank"
							rel="noopener noreferrer"
							className="text-red-400 hover:text-red-300 underline text-lg font-semibold inline-block mb-2"
						>
							C/ de la Ribera de Curtidores, 26
							<FontAwesomeIcon icon={faExternalLinkAlt} className="ml-2 text-sm" />
						</a>
						).
					</>,
					<>
						A pocos pasos de los metros de Embajadores y Acacias, el local cuenta con mesas amplias
						perfectamente adaptadas para jugar cómodamente durante horas, con espacio para todos tus
						materiales de juego.
					</>,
				],
			},
			features: [
				"Ubicación céntrica y bien comunicada",
				"Mesas amplias para jugar cómodamente",
				"Ambiente relajado y orientado al juego",
				"Amplia selección de juegos de mesa disponibles",
				"Personal amable y conocedor del mundo friki",
			],
			image: "/images/rol-en-replay-venue.webp",
			color: "border-blue-500",
			accentColor: "bg-blue-600",
			textColor: "text-blue-400",
		},
	];

	const benefits = [
		{
			icon: faGamepad,
			title: "Experiencia Completa",
			description: "Todo incluido: partida, comida, bebida y materiales. Solo trae tus ganas de divertirte.",
		},
		{
			icon: faUsers,
			title: "Ambiente Social",
			description: "Pásalo bien con tus colegas en un entorno diseñado para el juego y la diversión.",
		},
		{
			icon: faUtensils,
			title: "Comida de Calidad",
			description: "Disfruta de un menú especialmente pensado para jugadores, sin interrumpir la partida.",
		},
		{
			icon: faDice,
			title: "Material Incluido",
			description: "No necesitas traer nada, nosotros proveemos todos los materiales necesarios para jugar.",
		},
	];

	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/rol-en-replay.webp')" }}
			>
				<div className="absolute inset-0 bg-black/70"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">Rol en Replay</span>
					</motion.h1>

					<motion.p
						className="text-2xl text-gray-300 max-w-3xl mx-auto text-center mb-8"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="text-red-400">
							Noche de Rol + Festín de Héroes
							<br />
							<span className="text-xl italic text-gray-300">
								Una experiencia única que combina lo mejor de los juegos de rol con una cena informal en un
								ambiente perfecto para jugar.
							</span>
						</strong>
					</motion.p>

					{/* YouTube Video Embed */}
					<motion.div
						className="mb-8 aspect-w-16 aspect-h-9 w-full max-w-4xl mx-auto"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.4, duration: 0.6 }}
					>
						<div
							className="relative overflow-hidden rounded-lg shadow-lg"
							style={{ paddingBottom: "56.25%" }}
						>
							<iframe
								className="absolute top-0 left-0 w-full h-full"
								src="https://www.youtube.com/embed/Sq1VNHFtdFU"
								title="YouTube video player"
								frameBorder="0"
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
								allowFullScreen
							></iframe>
						</div>
					</motion.div>

					<motion.div
						className="text-center"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.6, duration: 0.6 }}
					>
						<p className="text-xl text-gray-200 max-w-2xl mx-auto mb-6">
							<FontAwesomeIcon icon={faClock} className="mr-2 text-red-400" />
							<strong>4 horas de juego</strong> + <strong>cena incluida</strong> por solo{" "}
							<span className="font-amarante text-red-400">50€ por persona</span>
						</p>
						<motion.a
							href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, me gustaría reservar para el Rol en Replay. ¿Tenéis disponibilidad?")}`}
							className="text-lg text-white bg-red-800/50 rounded-lg py-2 px-4 inline-block"
							whileHover={{
								scale: 1.05,
								boxShadow: "0 0 15px #ffffff",
							}}
							whileTap={{ scale: 0.98 }}
							target="_blank"
							rel="noopener noreferrer"
						>
							<FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
							¡Reserva ya tu plaza para una noche épica de rol y gastronomía!
						</motion.a>
					</motion.div>
				</div>
			</section>

			{/* Benefits Overview */}
			<section className="py-12 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faStar} className="mr-3 text-red-500" />
						¿Por qué elegir el Rol en Replay?
					</motion.h2>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
						{benefits.map((benefit, index) => (
							<motion.div
								key={index}
								className="bg-gray-800 rounded-lg p-6 flex items-start gap-4"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ delay: index * 0.1, duration: 0.5 }}
							>
								<div className="bg-red-600 p-3 rounded-full flex-shrink-0">
									<FontAwesomeIcon icon={benefit.icon} className="text-white text-xl" />
								</div>
								<div>
									<h3 className="text-xl font-amarante text-red-400 mb-2">{benefit.title}</h3>
									<p className="text-gray-300">{benefit.description}</p>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* Main Features Section */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faGamepad} className="mr-3 text-red-500" />
						Una Experiencia Completa
					</motion.h2>

					<div className="flex flex-col gap-16">
						{experienceFeatures.map((feature, index) => (
							<motion.div
								key={feature.id}
								className={`bg-gray-800 rounded-lg overflow-hidden border-l-4 ${feature.color}`}
								initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true }}
								transition={{ delay: index * 0.2, duration: 0.6 }}
							>
								<div className="p-8">
									<div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
										<div
											className={`w-20 h-20 ${feature.accentColor} rounded-full flex items-center justify-center flex-shrink-0 shadow-lg`}
										>
											<FontAwesomeIcon icon={feature.icon} className="text-white text-3xl" />
										</div>
										<div>
											<h3 className={`text-3xl font-amarante headerFont ${feature.textColor}`}>
												{feature.title}
											</h3>
										</div>
									</div>

									<div className="space-y-6">
										{feature.description.paragraphs.map((paragraph, idx) => (
											<p key={idx} className="text-gray-300">
												{paragraph}
											</p>
										))}

										<div className="mt-6">
											<h4 className="text-xl font-amarante text-red-300 mb-4">
												<FontAwesomeIcon icon={faStar} className="mr-2" />
												Características:
											</h4>
											<ul className="list-none grid grid-cols-1 md:grid-cols-2 gap-3">
												{feature.features.map((feat, idx) => (
													<li key={idx} className="text-gray-300 flex items-start">
														<span className="text-red-400 mr-2">•</span> {feat}
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* Pricing Section */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faTicketAlt} className="mr-3 text-red-500" />
						Detalles y Precios
					</motion.h2>

					<motion.div
						className="bg-gray-800 rounded-lg p-8 border-l-4 border-red-600"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
					>
						<div className="flex items-center gap-4 mb-6">
							<div className="bg-red-600 p-4 rounded-full">
								<FontAwesomeIcon icon={faGamepad} className="text-white text-2xl" />
							</div>
							<h3 className="text-2xl font-amarante headerFont text-red-400">Noche de Rol en Replay</h3>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
							<div>
								<h4 className="text-xl font-bold text-white mb-4">Incluye:</h4>
								<ul className="space-y-3">
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">4 horas de juego dirigido por un Amo del Calabozo</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Hamburguesa deliciosa + nachos riquísimos + bebida</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Uso de materiales (dados, fichas, etc.)</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Mesa amplia y cómoda para jugar</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Ambiente perfecto para jugadores de rol</span>
									</li>
								</ul>
							</div>
							<div>
								<h4 className="text-xl font-bold text-white mb-4">Horarios:</h4>
								<ul className="space-y-3">
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Lunes: Cerrado</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Martes: 17:30 - 23:30</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Miércoles: 17:30 - 23:30</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Jueves: 17:30 - 23:30</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Viernes: 12:00 - 01:00, 17:30 - 01:00</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Sábado: 12:30 - 01:00</span>
									</li>
									<li className="flex items-start">
										<span className="text-red-400 mr-2 mt-1">•</span>
										<span className="text-gray-300">Domingo: 12:30 - 23:30</span>
									</li>
								</ul>
							</div>
						</div>

						<div className="mt-8 text-center">
							<span className="text-4xl font-bold text-white">50€</span>
							<span className="text-xl text-gray-300 block">por persona (todo incluido)</span>
						</div>
					</motion.div>

					<motion.div
						className="mt-8 bg-gray-800 rounded-lg p-6 border-l-4 border-red-600"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ delay: 0.2 }}
					>
						<div className="flex items-center gap-4 mb-4">
							<div className="bg-red-600 p-3 rounded-full">
								<FontAwesomeIcon icon={faMapMarkerAlt} className="text-white text-xl" />
							</div>
							<h3 className="text-2xl font-amarante headerFont text-red-400">Ubicación</h3>
						</div>
						<div className="space-y-4">
							<div>
								<a
									href="https://www.replayoutletcafe.com/"
									target="_blank"
									rel="noopener noreferrer"
									className="text-red-400 hover:text-red-300 underline text-lg font-semibold inline-block mb-2"
								>
									Replay Boardgame Café
									<FontAwesomeIcon icon={faExternalLinkAlt} className="ml-2 text-sm" />
								</a>
								<br />
								<a
									href="https://www.google.com/maps?q=Replay+Boardgame+Caf%C3%A9"
									target="_blank"
									rel="noopener noreferrer"
									className="text-red-400 hover:text-red-300 underline text-lg font-semibold inline-block mb-2"
								>
									C/ de la Ribera de Curtidores, 26
									<FontAwesomeIcon icon={faExternalLinkAlt} className="ml-2 text-sm" />
								</a>
								<p className="text-gray-300">
									28005 Madrid
									<br />
									Metro: Embajadores (L3) y Acacias (L5)
								</p>
							</div>
							<div className="flex flex-col sm:flex-row gap-4">
								<a
									href="https://www.google.com/maps?q=Replay+Boardgame+Caf%C3%A9"
									target="_blank"
									rel="noopener noreferrer"
									className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-2 text-center justify-center"
								>
									<FontAwesomeIcon icon={faMapMarkerAlt} />
									Ver en Google Maps
									<FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
								</a>
								<a
									href="https://www.replayoutletcafe.com/"
									target="_blank"
									rel="noopener noreferrer"
									className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-2 text-center justify-center"
								>
									<FontAwesomeIcon icon={faGamepad} />
									Visitar Web del Café
									<FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
								</a>
							</div>
						</div>
						<p className="text-gray-300 mt-4">
							Un espacio acogedor perfectamente adaptado para jugar cómodamente, con todas las comodidades que
							necesitas para una noche épica de rol.
						</p>
					</motion.div>
				</div>
			</section>

			{/* Final CTA */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="bg-gray-800 rounded-lg p-8 text-center border-4 border-red-700"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<motion.div
							whileHover={{ scale: 1.05 }}
							className="mx-auto w-24 h-24 bg-red-600 rounded-full flex items-center justify-center mb-6"
						>
							<FontAwesomeIcon icon={faDice} className="text-white text-4xl" />
						</motion.div>
						<h2 className="text-3xl font-amarante headerFont text-red-400 mb-4">
							¡No te quedes sin tu plaza!
						</h2>
						<p className="text-gray-300 text-xl mb-8">
							Las plazas son limitadas para garantizar la mejor experiencia de juego. Reserva ahora tu noche
							épica de rol y gastronomía en el{" "}
							<a
								href="https://www.replayoutletcafe.com/"
								target="_blank"
								rel="noopener noreferrer"
								className="text-red-400 hover:text-red-300 underline"
							>
								Replay Boardgame Café
							</a>
							.
						</p>
						<motion.div
							className="flex flex-col md:flex-row items-center justify-center gap-4"
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
						>
							<motion.a
								href="mailto:somos@losamosdelcalabozo.com?subject=Reserva para Rol en Replay"
								className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.1,
									boxShadow: "0 0 15px #3b82f6",
									backgroundColor: "white",
									color: "black",
									fontWeight: "900",
									fontStyle: "italic",
								}}
								whileTap={{ scale: 0.98 }}
							>
								<FontAwesomeIcon icon={faEnvelope} className="mr-2" />
								Reservar por Email
							</motion.a>

							<motion.a
								href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, me gustaría reservar para el Rol en Replay. ¿Tenéis disponibilidad?")}`}
								className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.05,
									boxShadow: "0 0 15px #22c55e",
								}}
								whileTap={{ scale: 0.98 }}
								target="_blank"
								rel="noopener noreferrer"
							>
								<FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
								Reservar por WhatsApp
							</motion.a>
						</motion.div>
					</motion.div>
				</div>
			</section>

			{/* Main Contact Methods */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					{/* Email Address Display */}
					<motion.div
						className="mt-12 bg-gray-800 rounded-lg p-8 text-center relative"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.8, duration: 0.6 }}
					>
						<h3 className="text-2xl font-amarante headerFont text-red-400 mb-4">Contacto Directo</h3>
						<motion.div
							className="text-2xl md:text-4xl font-bold text-white bg-gray-700 py-4 px-2 rounded-lg select-all cursor-pointer"
							whileHover={{
								backgroundColor: "#3b82f6",
								color: "white",
								transition: { duration: 0.3 },
							}}
							onClick={() => {
								navigator.clipboard.writeText("somos@losamosdelcalabozo.com");
								setShowCopyNotification(true);
								setTimeout(() => setShowCopyNotification(false), 2000);
							}}
						>
							somos@losamosdelcalabozo.com
						</motion.div>
						<p className="text-gray-400 mt-2 text-sm italic">
							Haz click para copiar nuestro email de contacto
						</p>
						<AnimatePresence>
							{showCopyNotification && (
								<motion.div
									className="bg-green-600 text-white py-2 px-4 rounded-md absolute left-1/2 transform -translate-x-1/2 mt-2"
									initial={{ opacity: 0, y: -10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0 }}
								>
									Email copiado al portapapeles
								</motion.div>
							)}
						</AnimatePresence>
					</motion.div>
				</div>
			</section>

			{/* Easter Egg */}
			<section className="py-4 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="text-center opacity-30 hover:opacity-100 transition-opacity cursor-pointer"
						whileHover={{ scale: 1.01 }}
						whileTap={{ scale: 0.99 }}
					>
						<p className="text-sm text-gray-500 italic">
							"El verdadero héroe no es el que derrota al dragón, sino el que comparte sus nachos con el grupo
							mientras lo hace."
						</p>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default RolEnReplayPage;
